import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components/macro";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CircularIndeterminate } from "../components/Progress";

import {
  CardContent,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  Divider as MuiDivider,
  Typography as MuiTypography,
  Alert as MuiAlert,
  Button,
} from "@mui/material";
import { spacing, display } from "@mui/system";
import axios from "../../utils/axios";
import HomeIcon from "@mui/icons-material/Home";
import convertUTC from "../../utils/convertUTC";
import convertUSD from "../../utils/convertUSD";
import StatusChip from "../../components/StatusChip";
import { COLOR_DEPOSIT_STATUSES } from "../../constants";
import DepositInfo from "./components/DepositInfo";

const Card = styled(MuiCard)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(display);

const Alert = styled(MuiAlert)(spacing);

function DepositView() {
  const { depositId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [options, setOptions] = useState({});
  const [deposit, setDeposit] = useState({
    amount: null,
    captureId: null,
    createdAt: null,
    status: null,
    userId: null,
    id: null,
  });
  const [refund, setRefund] = useState(null);

  const getDeposit = useCallback(async () => {
    try {
      setLoading(true);

      const { data: options } = await axios.options(
        `/api/v1/deposits/${depositId}`
      );
      setOptions(options);

      const { data: deposit } = await axios.get(
        `/api/v1/deposits/${depositId}`
      );
      setDeposit(deposit);

      if (deposit.refundId) {
        const { data: refund } = await axios.get(
          `/api/v1/refunds/${deposit.refundId}`
        );
        setRefund(refund);
      }

      setLoading(false);
    } catch (e) {
      setError(e.message);
      console.log(e);
    }
  }, [setLoading, setError, setDeposit, depositId]);

  useEffect(() => {
    getDeposit();
  }, [getDeposit]);

  if (error) {
    return (
      <Alert mb={4} severity="error">
        {error}
      </Alert>
    );
  }

  return (
    <React.Fragment>
      <Helmet title="User View" />

      <Typography variant="h3" gutterBottom display="inline">
        Deposit ID {depositId}
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          <HomeIcon />
        </Link>
        <Link component={NavLink} to="/deposits">
          Deposits
        </Link>
        <Typography>{depositId}</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Card>
        <CardContent>
          <Grid container spacing={8}>
            <Grid item xs={12}>
              <Grid
                container
                spacing={{ xs: 8, md: 12, lg: 20 }}
                direction="row"
              >
                <Grid item xs="auto">
                  <Grid container spacing={4} direction="column">
                    <Grid item xs={12}>
                      <DepositInfo
                        loading={loading}
                        title="Deposit ID"
                        value={deposit.id}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <DepositInfo
                        loading={loading}
                        title="Braintree Transaction ID"
                        value={deposit.transactionId}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <DepositInfo
                        loading={loading}
                        title="User ID"
                        value={deposit.userId}
                        link={`/users/${deposit.userId}`}
                      />
                    </Grid>
                    {refund && (
                      <Grid item xs={12}>
                        <DepositInfo
                          loading={loading}
                          title="Refund ID"
                          value={deposit.refundId}
                          link={`/refunds/${deposit.refundId}`}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid item xs="auto">
                  <Grid container spacing={4} direction="column">
                    <Grid item xs={12}>
                      <DepositInfo
                        loading={loading}
                        title="Deposit Amount"
                        value={convertUSD(deposit.amount)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <DepositInfo
                        loading={loading}
                        title="Deposit Source"
                        value={deposit.source}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <DepositInfo
                        loading={loading}
                        title="Deposit Status"
                        value={
                          <StatusChip
                            options={COLOR_DEPOSIT_STATUSES}
                            value={deposit.status}
                            label={
                              options?.status?.find(
                                (entry) => entry.value === deposit.status
                              )?.display_name
                            }
                            size="small"
                            color="success"
                          />
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
                {refund && (
                  <Grid item xs="auto">
                    <Grid container spacing={4} direction="column">
                      <Grid item xs={12}>
                        <DepositInfo
                          loading={loading}
                          title="User Real Cash Amount for the Moment"
                          value={convertUSD(refund.userRealCashAmountForMoment)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <DepositInfo
                          loading={loading}
                          title="Reason for the Refund"
                          value={refund.reasonRefund}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <DepositInfo
                          loading={loading}
                          title="Additional Information"
                          value={refund.reasonText}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                <Grid item xs="auto">
                  <Grid container spacing={4} direction="column">
                    <Grid item xs={12}>
                      <DepositInfo
                        loading={loading}
                        title="Deposit date/time"
                        value={convertUTC(deposit.createdAt)}
                      />
                    </Grid>
                    {refund && (
                      <Grid item xs={12}>
                        <DepositInfo
                          loading={loading}
                          title="Refund date/time"
                          value={convertUTC(refund.createdAt)}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={4}>
                {!refund && (
                  <Grid item>
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={() => navigate(`/deposits/${depositId}/refund`)}
                      disabled={loading}
                    >
                      Refund
                    </Button>
                  </Grid>
                )}
                <Grid item>
                  <Button
                    color="primary"
                    variant={refund ? "contained" : "text"}
                    onClick={() => window.open(deposit.invoice, "_blank")}
                    disabled={loading}
                  >
                    Print the payment invoice into PDF
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </React.Fragment>
  );
}

export default DepositView;
